import { useCallback, useRef } from 'react';

import {
  CollectionEntriesDocument,
  CollectionsDocument,
  isError,
  useCreateCollectionEntryMutation,
  useCreateCollectionWithIdMutation,
  useDeleteCollectionEntryMutation,
} from '@spotify/confidence-graphql';
import { useCurrentIdentity } from '@spotify/plugin-confidence-iam-react';

import {
  deriveFavoritesCollectionName,
  makeFavoritesCollectionId,
} from '../utils';

export const useFavoritesCollection = () => {
  const { currentIdentity } = useCurrentIdentity();

  const favoritesCollectionName = currentIdentity?.name
    ? deriveFavoritesCollectionName(currentIdentity.name)
    : undefined;

  const favoritesCollectionId = currentIdentity?.name
    ? makeFavoritesCollectionId(currentIdentity.name)
    : undefined;

  const [createCollectionWithId] = useCreateCollectionWithIdMutation();
  const [createEntry] = useCreateCollectionEntryMutation({
    refetchQueries: [CollectionEntriesDocument],
    awaitRefetchQueries: true,
  });
  const [deleteEntry] = useDeleteCollectionEntryMutation();

  // Store the in-flight creation promise so concurrent callers can await it
  const creatingPromiseRef = useRef<Promise<void> | null>(null);

  const ensureCollection = useCallback(async () => {
    if (creatingPromiseRef.current) {
      await creatingPromiseRef.current;
      return;
    }
    if (!favoritesCollectionId) return;

    creatingPromiseRef.current = (async () => {
      try {
        const result = await createCollectionWithId({
          variables: {
            input: { displayName: 'Favorites', isPrivate: true },
            collectionId: favoritesCollectionId,
          },
          refetchQueries: [CollectionsDocument],
          awaitRefetchQueries: true,
        });
        // ALREADY_EXISTS is fine - another tab or concurrent call may have created it
        if (isError(result.data?.createCollection)) {
          const error = result.data?.createCollection;
          if ('code' in error && error.code !== 'ALREADY_EXISTS') {
            throw new Error(
              error.message ?? 'Failed to create favorites collection',
            );
          }
        }
      } finally {
        creatingPromiseRef.current = null;
      }
    })();

    await creatingPromiseRef.current;
  }, [favoritesCollectionId, createCollectionWithId]);

  const addToFavorites = useCallback(
    async (resourceName: string) => {
      if (!favoritesCollectionName) return;
      await ensureCollection();
      await createEntry({
        variables: {
          parent: favoritesCollectionName,
          collectionEntry: { resource: resourceName },
        },
      });
    },
    [favoritesCollectionName, ensureCollection, createEntry],
  );

  const removeFromFavorites = useCallback(
    async (entryName: string) => {
      await deleteEntry({
        variables: { name: entryName },
        update: cache => {
          cache.evict({
            id: cache.identify({
              __typename: 'CollectionsV1CollectionEntry',
              name: entryName,
            }),
          });
          cache.gc();
        },
      });
    },
    [deleteEntry],
  );

  return {
    addToFavorites,
    removeFromFavorites,
    ensureCollection,
    favoritesCollectionName,
  };
};
